import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import getDefaultStart from 'owa-calendar-forms-common/lib/utils/getDefaultStart';
import {
    addCalendarItems,
    CalendarSurfaceStore,
    removeCalendarItems,
} from 'owa-calendar-surface-dategrid';
import { defaultCommandTimeout } from 'owa-calendar-command-executor';
import { OwaDate, getDateString } from 'owa-datetime';
import { findScrollableParent } from '@fluentui/utilities';
import {
    getCalendarColorScheme,
    getCalendarColorSchemeByFolderId,
} from 'owa-calendar-color-resolver';
import {
    getCalendarFolderId,
    getFolderIdByCalendarID,
    getCalendarIdByFolderId,
    isWritableCalendar,
    getCalendarEntries,
} from 'owa-calendar-cache';
import { getGuid } from 'owa-guid';
import { tryShowQuickCompose } from 'owa-calendar-quick-compose/lib/actions/tryShowQuickCompose';
import { makePreviewItemId } from './previewItemUtils';
import EventScope from 'owa-service/lib/contract/EventScope';
import { mutatorAction } from 'satcheljs';
import { getIsMsHighContrast } from 'owa-high-contrast';
import type { CalendarEntry, CalendarId } from 'owa-graph-schema';

const SURFACE_ENTRY_SOURCE = 'Surface';
const PEEK_DISABLE_TIMEOUT = 600;

let isEnabled: boolean = true;

interface QuickComposePreviewItemState {
    item: CalendarEvent;
    surfaceStore: CalendarSurfaceStore;
}

interface QuickComposeMonthTargetState {
    target: HTMLElement;
    targetOriginalStyle: React.CSSProperties;
}

let quickComposePreviewItemState: QuickComposePreviewItemState | null;
let quickComposeMonthTargetState: QuickComposeMonthTargetState | null;

export const updatePreviewColor = mutatorAction('updatePreviewColor', (calendarId: CalendarId) => {
    if (quickComposePreviewItemState) {
        let folderId = getFolderIdByCalendarID(calendarId.id);
        if (folderId) {
            quickComposePreviewItemState.item.ParentFolderId.Id = folderId;
        }
    }
    if (quickComposeMonthTargetState) {
        highlightQuickComposeTarget(
            quickComposeMonthTargetState.target,
            getCalendarColorScheme(calendarId)
        );
    }
});

export interface QuickAddState {
    calendars: CalendarEntry[];
    surfaceStore: CalendarSurfaceStore;
}

/**
 * Attaches quick compose to month day cell header and highlights the header
 * @param {OwaDate} date the date of month cell
 * @param {HTMLElement} clickTarget the month cell html element
 */
export function monthQuickAdd(date: OwaDate, clickTarget: HTMLElement): void {
    if (!isEnabled) {
        return;
    }

    if (quickComposeMonthTargetState) {
        clearPreviewItem();
    }

    let calendar = getAllWritableCalendars()[0];
    if (!calendar) {
        // No writable calendars to create an item on
        return;
    }

    let folderId = getCalendarFolderId(calendar);
    let colorScheme = getCalendarColorSchemeByFolderId(folderId);

    let target: HTMLDivElement = document.getElementById(
        'header_' + getDateString(date)
    ) as HTMLDivElement;

    quickComposeMonthTargetState = {
        target: target,
        targetOriginalStyle: {
            outline: target.style.outline,
            backgroundColor: target.style.backgroundColor,
        },
    };

    let initialCalendarEvent = {
        Start: getDefaultStart(date),
        IsAllDayEvent: false,
    } as CalendarEvent;

    highlightQuickComposeTarget(target, colorScheme);

    tryShowQuickCompose(initialCalendarEvent, SURFACE_ENTRY_SOURCE, EventScope.Default, target);
}

/**
 * Creates new item on surface and attaches it with QuickCompose form
 * @param {OwaDate} start - item's start date time
 * @param {OwaDate} end - item's end date time
 * @param {boolean} isAllDay - isAllDay flag
 * @param {boolean} isClickAndDragEvent - boolean for event type (true for click and drag / false for single click on surface)
 */
export function createQuickComposePreviewItem(
    surfaceStore: CalendarSurfaceStore,
    start: OwaDate,
    end: OwaDate,
    isAllDay: boolean,
    isClickAndDragEvent: boolean,
    selectedCalendarFolderId?: string
): void {
    if (!isEnabled) {
        return;
    }

    if (quickComposePreviewItemState) {
        clearPreviewItem();
    }

    let calendar: CalendarEntry | null = null;

    if (selectedCalendarFolderId) {
        let selectedCalendarId = getCalendarIdByFolderId(selectedCalendarFolderId);
        calendar = selectedCalendarId
            ? getCalendarEntries().filter(c => c.calendarId.id == selectedCalendarId?.id)[0]
            : null;
        // if provided calendar is not writable, return
        if (!calendar || !isWritableCalendar(calendar)) {
            return;
        }
    }

    if (!calendar) {
        calendar = getAllWritableCalendars()[0];
    }
    if (!calendar) {
        // No writable calendars to create an item on
        return;
    }

    // generate random item id for preview item
    let previewItemId = makePreviewItemId();

    let newItemGuid = getGuid();

    const previewItemSelector = `div[data-calitemid="${previewItemId}"]`;

    quickComposePreviewItemState = {
        item: {
            ItemId: { Id: previewItemId },
            ParentFolderId: {
                Id: selectedCalendarFolderId
                    ? selectedCalendarFolderId
                    : getCalendarFolderId(calendar),
            },
            ClientSeriesId: newItemGuid,
            Start: start,
            End: end,
            IsAllDayEvent: isAllDay,
            FreeBusyType: 'Busy',
        } as CalendarEvent,
        surfaceStore: surfaceStore,
    };

    addCalendarItems([quickComposePreviewItemState.item], surfaceStore, false /* replaceAll */);

    let initialCalendarEvent = {
        Start: quickComposePreviewItemState.item.Start,
        IsAllDayEvent: quickComposePreviewItemState.item.IsAllDayEvent,
    } as CalendarEvent;

    // set ParentFolderId if this is quick compose is triggered for a specific calendar only,
    // otherwise quick compose internal logic will  decide which calendar to default to
    if (selectedCalendarFolderId) {
        initialCalendarEvent.ParentFolderId = {
            Id: quickComposePreviewItemState.item.ParentFolderId.Id,
            mailboxInfo: calendar.calendarId.mailboxInfo,
        };
    }

    // Click and drag scenario, setting the end time to avoid using the default meeting duration
    // to compute the end time in initialize calendar event
    if (isClickAndDragEvent) {
        initialCalendarEvent.End = quickComposePreviewItemState.item.End;
    }

    window.setTimeout(() => {
        // Ensure the new calendar event is in the viewport before the quickCompost preview is shown if its an All Day event which can be a long list.
        if (isAllDay) {
            let itemEl = document.querySelector(previewItemSelector) as HTMLElement;

            if (!itemEl) {
                // if the item still doesn't exist, exit
                return;
            }

            // calling itemEl.scrollIntoView() here breaks IE because IE11 scrolls the entire page horizontally
            // see https://social.technet.microsoft.com/Forums/ie/en-US/b8458796-8360-4512-b88a-62190f421ef7/issues-with-scrollintoview-javascript-method-in-ie10?forum=ieitprocurrentver
            let scrollParent = findScrollableParent(itemEl) as HTMLElement;

            itemEl.offsetTop + itemEl.offsetHeight;

            let currentParentTop = scrollParent.scrollTop;
            let currentParentBottom = scrollParent.scrollTop + scrollParent.clientHeight;
            let itemTop = itemEl.offsetTop;
            let itemBottom = itemEl.offsetTop + itemEl.clientHeight;

            if (scrollParent && (currentParentTop > itemTop || currentParentBottom < itemBottom)) {
                scrollParent.scrollTop = itemEl.offsetTop;
            }
        }

        tryShowQuickCompose(
            initialCalendarEvent,
            SURFACE_ENTRY_SOURCE,
            EventScope.Default,
            previewItemSelector
        );
    }, 0);
}

export function clearPreviewOnComposeDismiss() {
    toggleEnable();
    clearPreviewItem();
}

// The timeout for disabling subsequent quick compose popup should be 300 ms more the timeout to wait for processing a new single click,
// in order to avoid scenarios where there is a delay in receiving the a new single click
export function toggleEnable() {
    if (isEnabled) {
        isEnabled = false;
        window.setTimeout(() => {
            isEnabled = true;
        }, defaultCommandTimeout + PEEK_DISABLE_TIMEOUT);
    }
}

function clearPreviewItem() {
    if (quickComposePreviewItemState) {
        removeCalendarItems(
            [quickComposePreviewItemState.item.ItemId.Id],
            quickComposePreviewItemState.surfaceStore
        );
        quickComposePreviewItemState = null;
    }

    if (quickComposeMonthTargetState) {
        restoreQuickComposeTargetStyle();
        quickComposeMonthTargetState = null;
    }
}

function highlightQuickComposeTarget(target: HTMLElement, colorScheme: ColorScheme) {
    target.style.outline = '1px solid';
    if (!getIsMsHighContrast()) {
        target.style.backgroundColor = colorScheme.calLighter;
        target.style.outlineColor = colorScheme.calLight;
    } else {
        target.style.outlineColor = 'window';
    }
}

function restoreQuickComposeTargetStyle() {
    if (quickComposeMonthTargetState) {
        let { target, targetOriginalStyle } = quickComposeMonthTargetState;
        target.style.backgroundColor = targetOriginalStyle.backgroundColor as string;
        target.style.outline = targetOriginalStyle.outline as string;
        target.style.outlineColor = targetOriginalStyle.outlineColor as string;
    }
}

function getAllWritableCalendars(): CalendarEntry[] {
    return getCalendarEntries().filter(isWritableCalendar);
}
